import React, {useEffect} from 'react';
import {Button as ButtonBase, ButtonGroup, ClickAwayListener, Fade, Grid, IconButton, Paper, Popper, TableCell, Typography} from "@mui/material";
import {AddCircleOutline, Delete, Edit, History, Settings, Sync} from "@mui/icons-material";
import {Link} from "react-router-dom";
import Helpers, {getCompanyNames} from "../../../helpers";
import Auth from "../../../auth";
import moment from "moment";
import CatalogCheckbox from "../../../components/EnhancedCheckbox";
import {EventIdColumn} from "../../catalogmanager/containers/catalog/components/catalog_columns";
import {Root, Header, IconContainer, defaultResultsContainerStyles, pageContentGap} from "../../../theme";
import { styled } from '@mui/material/styles';
import {ClipboardCopyIcon, ExportIcon, ImportIcon} from "../../../utils/icons";
import CatalogTab from "../../catalogmanager/containers/catalog/components/catalog_tab";
import ContentLoader from "react-content-loader";
import EnhancedTable from "../../../components/table";
import CatalogEventResults from "../../catalogmanager/containers/catalog/components/catalog_event_results";
import CatalogManagerResults, {BulkUpdateStatus} from "../../../components/CatalogManagerResults";
import Config from "../../../config";
import EnhancedSelect from "../../../components/EnhancedSelect";
import InventoryEntity from './components/inventory_entity';
import {utilCheckInventory, utilDeleteInventory, utilGetInventorySummary, utilGetPartners, utilUpdateInventory} from '../../../utils/utils';
import RefreshSupplierDataModal from '../../catalogmanager/components/Dialogs/RefreshSupplierDataModal';
import { removeFiltersByField } from '../../../components/saved_search/utils/filterManager';
import axios from 'axios';
import EnhancedErrorDialog from '../../../components/EnhancedErrorDialog';
import EnhancedConfirmDialog from '../../../components/EnhancedConfirmDialog';
import UpdatingDialog from '../../../components/Dialogs/UpdatingDialog';
import EnhancedTextField from '../../../components/EnhancedTextField';
import {InventoryHistoryAnalyticsDialog as InventoryHistoryAnalyticsDialogBase} from '../../reports/analytics_dialogs';
import { Alert } from '@mui/material';
import { GetRuleDescription } from '../shared/validation';
import type { EdiSpecification, HelpDesk, InventoryEntityRow, InventorySummary, Partner, RouteComponentPropsLike, SelectOption, SetTitle, ValidationRuleGroup } from './types';

// Relax third-party / legacy types to `any` (behavior unchanged).
const Button: any = ButtonBase;
const InventoryHistoryAnalyticsDialog: any = InventoryHistoryAnalyticsDialogBase;

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
    '& .MuiButtonGroup-grouped': {
        borderColor: '#4F4D55',
        color: 'rgba(0, 0, 0, 0.87)',
        '&:hover': {
            borderColor: '#4F4D55',
            backgroundColor: theme.palette.grey[100],
        },
        '&.Mui-disabled': {
            borderColor: 'rgba(0, 0, 0, 0.26)',
            color: 'rgba(0, 0, 0, 0.26)',
        }
    }
}));

const iconButtonSx = {
    width: "100%",
    justifyContent: "flex-start",
    padding: "12px",
    textTransform: "none",
    borderRadius: "6px",
    border: "none",
    background: "#fff",
    boxShadow: "0px 2px 4px -1px rgba(10, 9, 11, 0.02), 0px 5px 13px -5px rgba(10, 9, 11, 0.05)"
};


interface InventoryFilter {
    field?: string;
    operator?: string;
    value?: any;
    filters?: InventoryFilter[];
    logic?: string;
    system?: boolean;
    [key: string]: any;
}

interface InventoryTableLike {
    state: {
        filter?: { filters?: InventoryFilter[]; [key: string]: any } | null;
        selected?: Array<string | number>;
        data?: InventoryEntityRow[];
        total?: number;
        [key: string]: any;
    };
    data?: InventoryEntityRow[];
    extensions: {
        replaceFilters: (filters: InventoryFilter[]) => void;
        onRemoveFilter?: (filter: InventoryFilter) => void;
        [key: string]: any;
    };
    [key: string]: any;
}

interface InventoryEventsStatusProps {
    partners: Partner[];
    refreshInventorySummary: (partnerId: string | number | null) => void;
    table: InventoryTableLike;
}
function InventoryEventsStatus(props: InventoryEventsStatusProps) {
    const {partners, refreshInventorySummary} = props;
    const {table} = props;

    const [selectedPartner, setSelectedPartner] = React.useState<SelectOption | null>(null);

    useEffect(() => {
        if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
            const filter = table.state.filter.filters.find((filter) => filter.field === "PartnerCoId");
            if (filter) {
                const partner = partners.find((p) => p.Id === filter.value);
                if (partner) {
                    setSelectedPartner({
                        label: partner.CompanyName,
                        value: partner.Id
                    });
                    refreshInventorySummary(partner.Id);
                } else {
                    setSelectedPartner(null);
                    refreshInventorySummary(null);
                }
            } else {
                setSelectedPartner(null);
                refreshInventorySummary(null);
            }
        } else {
            setSelectedPartner(null);
            refreshInventorySummary(null);
        }

        // eslint-disable-next-line
    }, [table.state.filter]);

    return (
        <>
            <EnhancedSelect
                isClearable
                placeholder={"Select partner..."}
                containerStyle={{minWidth: "100px"}}
                options={
                    partners.map((partner) => ({
                        label: partner.CompanyName,
                        value: partner.Id
                    }))
                }
                value={selectedPartner}
                onChange={(value) => {
                    if (!value) {
                        const oldFilters = (table.state.filter?.filters || []).filter(
                            (filter) => filter.field !== "PartnerCoId"
                        );
                        table.extensions.replaceFilters(oldFilters);
                        setSelectedPartner(null);
                        refreshInventorySummary(null);
                    } else {
                        const oldFilters = (table.state.filter?.filters || []).filter(
                            (filter) => filter.field !== "PartnerCoId"
                        );
                        table.extensions.replaceFilters([
                            ...oldFilters,
                            {
                                field: "PartnerCoId",
                                operator: 'eq',
                                value: value.value
                            }
                        ]);
                        setSelectedPartner(value);
                        refreshInventorySummary(value.value);
                    }
                }}
            />
        </>
    );
}

interface InventoryStatusProps {
    partners: Partner[];
    setInventoryStatusSelection: React.Dispatch<React.SetStateAction<string>>;
    inventoryStatusSelection: string;
    table: InventoryTableLike;
    isRetailer?: boolean;
    setTitle?: SetTitle;
    entityCount?: number;
}

function InventoryStatus(props: InventoryStatusProps) {
    const {partners, setInventoryStatusSelection, inventoryStatusSelection} = props;
    const {table, isRetailer, setTitle} = props;

    const [selectedPartner, setSelectedPartner] = React.useState<SelectOption | null>(null);
    const [entityCount, setEntityCount] = React.useState(0);
    
    const previousPartnerRef = React.useRef<string | null>(null);


    const detectInventoryFilterStatus = (filterState) => {
        if (!filterState || !filterState.filters || filterState.filters.length === 0) {
            return "all";
        }

        const simpleFilter = filterState.filters.find(filter => filter.field === "merchantSku");
        if (simpleFilter) {
            return simpleFilter.operator === "ne" ? "matched" : "unmatched";
        }

        const nestedFilter = filterState.filters.find(filter => 
            filter.filters && filter.filters.some(f => f.field === "merchantSku")
        );
        
        if (nestedFilter) {
            const hasNeFilter = nestedFilter.filters.some(f => 
                f.field === "merchantSku" && f.operator === "ne"
            );
            const hasEqNullFilter = nestedFilter.filters.some(f => 
                f.field === "merchantSku" && f.operator === "eq" && f.value === null
            );
            
            if (hasNeFilter) return "matched";
            if (hasEqNullFilter) return "unmatched";
        }

        return "all";
    };

    useEffect(() => {
        if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
            const filter = table.state.filter.filters.find((filter) => filter.operator === "eq" && filter.field === (isRetailer ? "supplierName" : "merchantName"));
            if (filter) {
                const partnerName = Array.isArray(filter.value) ? filter.value.join(", ") : filter.value;
                setSelectedPartner(
                    {
                        label: partnerName,
                        value: filter.value
                    }
                );
                
                if (setTitle && partnerName !== previousPartnerRef.current) {
                    const cleanPartnerName = partnerName.startsWith('Inv: ') 
                        ? partnerName.substring(5) 
                        : partnerName;
                    setTitle(`Inv: ${cleanPartnerName}`, true);
                    previousPartnerRef.current = partnerName;
                }
            } else {
                setSelectedPartner(null);
                if (setTitle && previousPartnerRef.current !== null) {
                    setTitle('Inventory Feed');
                    previousPartnerRef.current = null;
                }
            }
        } else {
            setSelectedPartner(null);
            if (setTitle && previousPartnerRef.current !== null) {
                setTitle('Inventory Feed');
                previousPartnerRef.current = null;
            }
        }

        const status = detectInventoryFilterStatus(table.state.filter);
        setInventoryStatusSelection(status);

        // eslint-disable-next-line
    }, [table.state.filter, isRetailer, setTitle]);

    useEffect(() => {
        const getEntityCount = async () => {
            const response = await axios.get(`${Config.api}/odata/Company/Functions.ItemSearch?$top=0&$count=true`);
            setEntityCount(response.data["@odata.count"]);
        }

        getEntityCount();
    }, []);

    return (
        <>
            <EnhancedSelect
                isClearable
                placeholder={"Select partner..."}
                containerStyle={{minWidth: "100px"}}
                options={
                    partners.map((partner) => ({
                        label: partner.CompanyName,
                        value: partner.Id
                    }))
                }
                value={selectedPartner}
                onChange={(value) => {
                    if (!value) {
                        table.extensions.onRemoveFilter({
                            field: isRetailer ? "supplierName" : "merchantName",
                            operator: 'eq',
                            value: selectedPartner.value
                        });
                    } else {
                        const oldFilters = table.state.filter?.filters?.filter((filter) => filter.field !== (isRetailer ? "supplierName" : "merchantName")) || [];
                        table.extensions.replaceFilters([
                            ...oldFilters,
                            {
                                field: isRetailer ? "supplierName" : "merchantName",
                                operator: 'eq',
                                value: value.label,
                            }
                        ]);
                    }

                    setSelectedPartner(value);
                }}
            />
            <>
                <Button
                    variant='outlined' color={inventoryStatusSelection === "all" ? "primary" : "default"}
                    size="small"
                    onClick={() => {
                        if (inventoryStatusSelection !== "all") {
                            if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
                                const cleanedFilter = removeFiltersByField(table.state.filter, "merchantSku");
                                table.extensions.replaceFilters(cleanedFilter.filters);
                            }
                        }

                        setInventoryStatusSelection("all");
                    }}
                >
                    All ({(entityCount || 0)})
                </Button>

                <Button
                    variant='outlined' color={inventoryStatusSelection === "matched" ? "primary" : "default"}
                    size="small"
                    onClick={() => {
                        if (inventoryStatusSelection !== "matched") {
                            if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
                                const cleanedFilter = removeFiltersByField(table.state.filter, "merchantSku");
                                const unmatchedFilter = {
                                    filters: [
                                        {
                                            field: "merchantSku",
                                            operator: "ne",
                                            value: null,
                                            system: true
                                        },
                                        {
                                            field: "merchantSku",
                                            operator: "ne",
                                            value: "",
                                            system: true
                                        }
                                    ],
                                    logic: "and"
                                };

                                const updatedFilters = [...cleanedFilter.filters, unmatchedFilter];
                                table.extensions.replaceFilters(updatedFilters);
                            } else {
                                const unmatchedFilter = {
                                    filters: [
                                        {
                                            field: "merchantSku",
                                            operator: "ne",
                                            value: null,
                                            system: true
                                        },
                                        {
                                            field: "merchantSku",
                                            operator: "ne",
                                            value: "",
                                            system: true
                                        }   
                                    ],
                                    logic: "and"
                                };

                                table.extensions.replaceFilters([unmatchedFilter]);
                            }
                        }

                        setInventoryStatusSelection("matched");
                    }}
                >
                    Matched SKUs
                </Button>

                <Button
                    variant='outlined' color={inventoryStatusSelection === "unmatched" ? "primary" : "default"}
                    size="small"
                    onClick={() => {
                        if (inventoryStatusSelection !== "unmatched") {
                            if (table.state.filter && table.state.filter.filters && table.state.filter.filters.length > 0) {
                                const cleanedFilter = removeFiltersByField(table.state.filter, "merchantSku");
                                const unmatchedFilter = {
                                    filters: [
                                        {
                                            field: "merchantSku",
                                            operator: "eq",
                                            value: null,
                                            system: true
                                        },
                                        {
                                            field: "merchantSku",
                                            operator: "eq",
                                            value: "",
                                            system: true
                                        }
                                    ],
                                    logic: "or"
                                };

                                const updatedFilters = [...cleanedFilter.filters, unmatchedFilter];
                                table.extensions.replaceFilters(updatedFilters);
                            } else {
                                const unmatchedFilter = {
                                    filters: [
                                        {
                                            field: "merchantSku",
                                            operator: "eq",
                                            value: null,
                                            system: true
                                        },
                                        {
                                            field: "merchantSku",
                                            operator: "eq",
                                            value: "",
                                            system: true
                                        }
                                    ],
                                    logic: "or",
                                };

                                table.extensions.replaceFilters([unmatchedFilter]);
                            }
                        }

                        setInventoryStatusSelection("unmatched");
                    }}
                >
                    Unmatched SKUs
                </Button>
            </>
        </>
    );
}

interface TableMoreOptsProps {
    row: InventoryEntityRow;
    setInventoryEntity: React.Dispatch<React.SetStateAction<InventoryEntityRow | null>>;
    setInventoryEditorOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setInventoryDeleteEntities: React.Dispatch<React.SetStateAction<InventoryEntityRow[]>>;
    setInventoryDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setInventoryBulkEntities: React.Dispatch<React.SetStateAction<InventoryEntityRow[]>>;
    setInventoryBulkOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    setHistoryAnalyticsData: React.Dispatch<React.SetStateAction<any>>;
    hasInventoryManagePermission: boolean;
    hasTieredReports: boolean;
}

function TableMoreOpts(props: TableMoreOptsProps) {
    const {row, setInventoryEntity, setInventoryEditorOpen, setInventoryDeleteEntities, setInventoryDeleteOpen, setInventoryBulkEntities, setHistoryAnalyticsData} = props;
    const {hasInventoryManagePermission, hasTieredReports} = props;


    return (
        <>
            {hasTieredReports && (
                <Button
                    sx={iconButtonSx}
                    onClick={(e) => {
                        setHistoryAnalyticsData(prev => ({
                            ...prev,
                            open: true,
                            viewMode: 'compact',
                            data: row
                        }));
                    }}
                >
                    <IconContainer>
                        <History style={{width: "20px", height: "20px"}}/>
                        <Typography>History</Typography>
                    </IconContainer>
                </Button>
            )}
            <Button
                sx={iconButtonSx}
                onClick={() => {
                    setInventoryEntity(row);
                    setInventoryEditorOpen(true);
                }}
            >
                <IconContainer>
                    <Edit style={{width: "20px", height: "20px"}}/>
                    <Typography>{hasInventoryManagePermission ? "Edit" : "View"}</Typography>
                </IconContainer>
            </Button>

            {hasInventoryManagePermission && (
                <Button
                    sx={iconButtonSx}
                    onClick={() => {
                        setInventoryBulkEntities([row]); // For zeroing out inventory
                        setInventoryDeleteEntities([row]);
                        setInventoryDeleteOpen(true);
                    }}
                >
                    <IconContainer>
                        <Delete style={{width: "20px", height: "20px"}}/>
                        <Typography>Delete</Typography>
                    </IconContainer>
                </Button>
            )}
        </>
    )
}

interface BulkActionsProps {
    table: InventoryTableLike;
    setInventoryDeleteEntities: React.Dispatch<React.SetStateAction<InventoryEntityRow[]>>;
    setInventoryDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setInventoryBulkEntities: React.Dispatch<React.SetStateAction<InventoryEntityRow[]>>;
    setInventoryBulkOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setInventoryBulkAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
    setInventoryUnmatchConfirm: React.Dispatch<React.SetStateAction<boolean>>;
    isBillable?: boolean;
    setConfig?: unknown;
    setEventConfig?: unknown;
}

export const BulkActions = (props: BulkActionsProps) => {
    const {
        setInventoryDeleteEntities, setInventoryDeleteOpen,
        setInventoryBulkEntities, setInventoryBulkOpen, setInventoryBulkAnchorEl,
        setInventoryUnmatchConfirm, isBillable
    } = props;

    return (
        <StyledButtonGroup variant="outlined"
                     style={{
                         visibility: props.table.state.selected.length > 0 ? "visible" : "hidden",
                         opacity: props.table.state.selected.length > 0 ? 1 : 0,
                     }}>
            <Button variant='outlined' onClick={(e) => {
                const selectedEntities = props.table.state.data.filter((row) => props.table.state.selected.includes(row.id));
                setInventoryBulkEntities(selectedEntities);
                setInventoryBulkOpen(true);
                setInventoryBulkAnchorEl(e.currentTarget);
            }}>
                Bulk Edit
            </Button>

            {isBillable && (
                <Button variant='outlined' onClick={(e) => {
                    const selectedEntities = props.table.state.data.filter((row) => props.table.state.selected.includes(row.id));
                    setInventoryBulkEntities(selectedEntities);
                    setInventoryUnmatchConfirm(true);
                }}>
                    Unmatch
                </Button>
            )}

            <Button variant='outlined' onClick={() => {
                const selectedEntities = props.table.state.data.filter((row) => props.table.state.selected.includes(row.id));
                setInventoryDeleteEntities(selectedEntities);
                setInventoryDeleteOpen(true);
            }}>
                Delete
            </Button>
        </StyledButtonGroup>
    )
}

interface BulkEditPopperProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    handleBulkEdit: (quantity: string | number | null, cost: string | number | null, merchantSku: string | null, status: string | null) => void;
    zeroOutInventory: () => void;
    setZeroOutConfirm: React.Dispatch<React.SetStateAction<boolean>>;
}

export function BulkEditPopper({
                            open,
                            anchorEl,
                            onClose,
                            handleBulkEdit,
                            zeroOutInventory,
                            setZeroOutConfirm
                        }: BulkEditPopperProps) {
    const [quantity, setQuantity] = React.useState(null);
    const [cost, setCost] = React.useState(null);
    const [status, setStatus] = React.useState(null);

    const [bulkEditConfirm, setBulkEditConfirm] = React.useState(false);
    const pendingRef = React.useRef<{quantity: number|null, cost: number|null, status: string|null}>({quantity: null, cost: null, status: null});

    const isValidStatus = () => !status || status.match(/^[a-zA-Z ]+$/); 

    useEffect(() => {
        setQuantity(null);
        setCost(null);
        setStatus(null);
    }, [open]);

    return (
        <>
            <Popper open={open} anchorEl={anchorEl} transition placement={"bottom-start"} style={{zIndex: 1300}}>
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={onClose} mouseEvent="onMouseDown">
                        <Fade {...TransitionProps} timeout={350}>
                            <BulkUpdateStatus container>
                                <Typography>Bulk edit selected items</Typography>

                                <EnhancedTextField
                                    label="Quantity"
                                    value={quantity}
                                    onChange={(e) => {
                                        const n = e.target.value === "" ? null : Number(e.target.value);
                                        if (n === null || Number.isFinite(n)) setQuantity(n);
                                    }}
                                    numeric={true}
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0
                                        }
                                    }}
                                />

                                <EnhancedTextField
                                    label="Cost"
                                    value={cost}
                                    onChange={(e) => {
                                        const n = e.target.value === "" ? null : Number(e.target.value);
                                        if (n === null || Number.isFinite(n)) setCost(n);
                                    }}
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0
                                        }
                                    }}
                                />

                                <EnhancedTextField
                                    label="Status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    placeholder={'Active, Discontinued...'}
                                    error={!isValidStatus() ? 'Status should not contain numbers or special characters.' : ''}
                                />

                                <Grid item container style={{width: "100%", gap: "8px"}} justifyContent={"flex-end"}>
                                    <Button
                                        style={{padding: "6px 12px"}}
                                        onClick={onClose}
                                        variant="outlined"
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        style={{padding: "6px 12px"}}
                                        onClick={() => {
                                            setZeroOutConfirm(true);
                                        }}
                                        variant="outlined"
                                    >
                                        Zero Out
                                    </Button>

                                    <Button
                                        color="primary"
                                        variant="outlined"
                                        style={{padding: "6px 12px"}}
                                        onClick={() => {
                                            if (quantity === null && cost === null && !status) return;
                                            pendingRef.current = {quantity, cost, status};
                                            setBulkEditConfirm(true);
                                        }}
                                        disabled={(quantity === null && cost === null && !status) || !isValidStatus()}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </BulkUpdateStatus>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            <EnhancedConfirmDialog
                open={bulkEditConfirm}
                setOpen={setBulkEditConfirm}
                title="Bulk Edit"
                message="This action cannot be undone. Are you sure you want to update the selected SKUs?"
                type={"caution"}
                action={(action) => {
                    if (action) {
                        handleBulkEdit(pendingRef.current.quantity, pendingRef.current.cost, null, pendingRef.current.status);
                    }
                    setBulkEditConfirm(false);
                    onClose();
                }}
            />
        </>
    );
}

export const SkuHoverTemplate = React.memo(({sku}: {sku?: string | null}) => {
    const [isHighlighted, setIsHighlighted] = React.useState(false);

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(sku);
        setIsHighlighted(true);
        setTimeout(() => setIsHighlighted(false), 1000);
    };

    return (
        <Grid container style={{flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "4px"}}>
            <Typography style={{
                backgroundColor: isHighlighted ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                transition: 'background-color 0.2s ease',
                padding: '2px 4px',
                borderRadius: '2px'
            }}>
                {sku}
            </Typography>
            {sku && (
                <IconButton
                    size="small"
                    onClick={handleCopy}
                    style={{ padding: "4px", marginBottom: "4px"}}
                >
                    <ClipboardCopyIcon width="18px" height="18px" />
                </IconButton>
            )}
        </Grid>
    );
});

interface InventoryManagementCenterProps extends RouteComponentPropsLike {
    isRetailer?: boolean;
    isBillable?: boolean;
    setTitle?: SetTitle;
    tieredReport?: unknown;
    signalr?: unknown;
}

function InventoryManagementCenter(props: InventoryManagementCenterProps) {
    let urlCatalog = new Helpers().getUrlObject('catalog');
    const {isBillable, setTitle} = props;

    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");
    const hasTieredReports = props.tieredReport != null;

    const filterNullExceptions = ["merchantSku"];

    const [inventoryConfig, setInventoryConfig] = React.useState({
        url: Config.api + "/odata/Company/Functions.ItemSearch",
        savedSearchEnabled: true,
        columns: [
            {id: "id", hidden: true},
            {id: "supplierId", hidden: true, type: 'number', label: 'Supplier ID'},
            {id: "merchantId", hidden: true, type: 'number', label: 'Retailer ID'},
            {
                id: 'supplierName',
                filterable: true,
                hidden: !props.isRetailer,
                label: 'Partner',
                width: '16em',
                getOptions: getCompanyNames
            },
            {
                id: 'merchantName',
                filterable: true,
                hidden: props.isRetailer,
                label: 'Partner',
                width: '16em',
                getOptions: getCompanyNames,
            },
            {
                id: 'supplierSku',
                sortable: true,
                filterable: true,
                label: 'Supplier SKU',
                width: '12em',
                toggleable: true,
                hoverTemplate: (row) => <SkuHoverTemplate sku={row.supplierSku} />,
                hoverTemplateInteractive: true,
                hoverTemplateLeaveDelay: 750
            },
            {
                id: 'merchantSku',
                sortable: true,
                filterable: true,
                label: 'Retailer SKU',
                width: '12em',
                toggleable: true,
                hoverTemplate: (row) => <SkuHoverTemplate sku={row.merchantSku} />,
                hoverTemplateInteractive: true,
                hoverTemplateLeaveDelay: 750
            },
            {id: 'quantity', sortable: true, type: 'number', filterable: true, label: 'Quantity', width: '8em'},
            {
                id: 'vendorNumber',
                toggleable: true,
                filterable: true,
                hidden: true,
                sortable: true,
                label: 'Vendor Number',
                width: '12em'
            },
            {id: 'upc', hidden: true, toggleable: true, sortable: true, filterable: true, label: 'UPC', width: '12em'},
            {
                id: 'manufacturerSku',
                hidden: true,
                toggleable: true,
                sortable: true,
                filterable: true,
                label: 'Mfr. SKU',
                width: '12em'
            },
            {
                id: 'cost',
                sortable: true,
                toggleable: true,
                type: 'number',
                filterable: true,
                label: 'Cost',
                width: '8em'
            },
            {
                id: 'retailPrice',
                sortable: true,
                toggleable: true,
                hidden: true,
                type: 'number',
                filterable: true,
                label: 'Retail Price',
                width: '8em'
            },
            {id: 'supplierDescription', toggleable: true, label: 'Description', align: 'left', width: '16em',
                hoverTemplate: (row) => {
                    return (
                        <Typography>
                            {row.supplierDescription}
                        </Typography>
                    )
                }
            },
            {
                id: 'size',
                sortable: true,
                toggleable: true,
                type: 'string',
                filterable: true,
                hidden: true,
                label: 'Size',
                width: '8em'
            },
            {
                id: 'color',
                sortable: true,
                toggleable: true,
                type: 'string',
                filterable: true,
                hidden: true,
                label: 'Color',
                width: '8em'
            },
            {
                id: 'style',
                sortable: true,
                toggleable: true,
                type: 'string',
                filterable: true,
                hidden: true,
                label: 'Style',
                width: '8em'
            },
            {
                id: 'weight',
                sortable: true,
                toggleable: true,
                type: 'number',
                filterable: true,
                hidden: true,
                label: 'Weight',
                width: '8em'
            },
            {id: 'warehouse', hidden: true, toggleable: true, filterable: true, label: 'Warehouse', width: '12em'},
            {
                id: 'nextAvailableDate',
                sortable: true,
                toggleable: true,
                type: 'string',
                filterable: true,
                hidden: true,
                label: 'Next Available Date',
                width: '16em'
            },
            {
                id: 'nextAvailableQuantity',
                sortable: true,
                toggleable: true,
                type: 'number',
                filterable: true,
                hidden: true,
                label: 'Next Available Qty',
                width: '12em'
            },
            {
                id: 'lastUpdate',
                type: 'date',
                toggleable: true,
                filterable: true,
                sortable: true,
                label: 'Refreshed within',
                template: (row) => {
                    const utcValue = moment.utc(row.lastUpdate);
                    return moment(utcValue).local().fromNow().replace(' ago', '');
                },
                width: '15em',
                hoverTemplate: (row) => {
                    return (
                        <Typography>
                            {moment(row.lastUpdate).format('MM/DD/YY hh:mmA')}
                        </Typography>
                    )
                },
                alwaysShowTooltip: true
            },
            {
                id: 'status',
                hidden: true,
                toggleable: true,
                sortable: true,
                filterable: true,
                label: 'Status',
                width: '12em'
            }
        ],
        keyField: 'id',
        order: 'asc',
        orderBy: 'supplierSku',
        pageSize: 25,
        pageSizes: [25, 50, 100],
        title: 'Inventory Search',
        refresh: false,
        filterInUrl: true,
        filter: null
    });
    const [eventsConfig, setEventsConfig] = React.useState({
        url: Config.api + "/odata/Company/VwActivityEvents",
        savedSearchEnabled: true,
        columns: [
            {
                id: 'EventDate',
                type: 'date',
                filterable: true,
                sortable: true,
                label: 'Date',
                template: (row, column, colIndex, rowIndex) => (
                    <Typography>{moment(row.EventDate).format('MM/DD/YYYY hh:mm A')}</Typography>
                ),
                width: '15em'
            },
            {id: 'PartnerCoId', label: "Partner CoId", hidden: true, filterable: false, sortable: true, width: '8em'},
            {
                id: 'PartnerCompanyName',
                filterable: true,
                sortable: true,
                label: 'Partner',
                width: '12em',
                alwaysShowTooltip: true,
                template: (row) => row.PartnerCompanyName || "All Partners",
                getOptions: getCompanyNames,
                hoverTemplate: (row) => {
                    return (
                        <Typography>
                            {row.PartnerCompanyName || "This event is not associated with a specific partner."}
                        </Typography>
                    )
                }
            },
            {id: 'EventLevel', label: 'Severity', width: '6em', filterable: true, sortable: true},
            {id: 'Summary', label: 'Summary', filterable: true, allowStringOperators: true, width: '16em'},
            {id: 'Details', label: 'Details', filterable: true, allowStringOperators: true, width: '16em',
                hoverTemplate: (row) => {
                    return (
                        <Typography>
                            {row.Details}
                        </Typography>
                    )
                }
            },
            {
                id: 'Viewed', label: "Viewed", sortable: true, hidden: true, filterable: false, template: (row, column, colIndex, rowIndex) => {
                    return (
                        <CatalogCheckbox checked={row.Viewed} disabled={true}/>
                    )
                }
            },
            {id: 'EventTypeId', hidden: true},
            {
                id: 'Id',
                width: '10em',
                stopPropagation: 'true',
                filterable: false,
                template: (row, column, colIndex, rowIndex, onAddFilter, setEventId, setEventViewed, setEventDialogOpen) =>
                    <EventIdColumn
                        row={row} setEventId={setEventId} setEventViewed={setEventViewed}
                        setEventDialogOpen={setEventDialogOpen}/>
            },
            {id: 'CustomerViewable', hidden: true, filterable: false},
        ],
        order: 'desc',
        orderBy: 'Id',
        keyField: 'Id',
        pageSize: 25,
        pageSizes: [25, 50, 100],
        title: "Event Log",
        refresh: false,
        filterInUrl: false,
        filter: {
            filters: [
                {field: "Category", operator: 'eq', value: 'Inventory', system: true},
            ], logic: 'and', system: true
        },
    });

    const [currentTab, setCurrentTab] = React.useState(urlCatalog || "feed");
    const [loading, setLoading] = React.useState(false);
    
    const inventoryTableRef = React.useRef(null);
    const eventsTableRef = React.useRef(null);

    const [inventoryEntity, setInventoryEntity] = React.useState<InventoryEntityRow | null>(null);
    const [inventoryEditorOpen, setInventoryEditorOpen] = React.useState(false);

    const [inventoryDeleteEntities, setInventoryDeleteEntities] = React.useState<InventoryEntityRow[]>([]);
    const [inventoryDeleteOpen, setInventoryDeleteOpen] = React.useState(false);
    const [inventoryDeleting, setInventoryDeleting] = React.useState(false);

    const [inventoryBulkEntities, setInventoryBulkEntities] = React.useState<InventoryEntityRow[]>([]);
    const [inventoryBulkOpen, setInventoryBulkOpen] = React.useState(false);
    const [inventoryBulkAnchorEl, setInventoryBulkAnchorEl] = React.useState<HTMLElement | null>(null);
    const [inventoryBulkUpdating, setInventoryBulkUpdating] = React.useState(false);
    const [inventoryZeroConfirm, setInventoryZeroConfirm] = React.useState(false);
    const [inventoryUnmatchConfirm, setInventoryUnmatchConfirm] = React.useState(false);

    const [inventoryStatusSelection, setInventoryStatusSelection] = React.useState("all");
    const [inventorySummary, setInventorySummary] = React.useState<InventorySummary>({
        lastUpdate: "",
        lastUpdateCount: ""
    });

    const [historyAnalyticsData, setHistoryAnalyticsData] = React.useState({ 
        anchor: {
            top: 0, 
            left: 0,
        },        
        data: null,
        viewMode: 'compact',
        open: false
    });
    

    const [error, setError] = React.useState({
        open: false,
        errorHeader: "",
        errorMessage: ""
    });

    const [partners, setPartners] = React.useState<Partner[]>([]);
    const [helpDesks, setHelpDesks] = React.useState<HelpDesk[]>([]);
    const [ediSpecifications, setEdiSpecifications] = React.useState<EdiSpecification[]>([]);
    const [rules, setRules] = React.useState<ValidationRuleGroup[]>([]);
    const [refreshModalOpen, setRefreshModalOpen] = React.useState(false);

    const updateTab = (tab, clearFilters) => {
        setCurrentTab(tab);

        if (clearFilters) {            
            // Clear all search params first, then set only the catalog param
            props.history.replace({
                pathname: props.location.pathname, 
                search: `?catalog=${tab}`
            });
        } else {
            new Helpers().setUrlObject('catalog', tab, props.history, props.location);
        }        
    };

    const loadPartners = async () => {
        setLoading(true);
        let partners = await utilGetPartners();
        if (partners.errorMessage) return;

        let sortedPartners = partners.sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        setPartners(sortedPartners);
        setLoading(false);
    }

    const deleteInventory = async () => {
        if (!hasInventoryManagePermission) return;

        setInventoryDeleting(true);
        let errors = [];

        try {
            for (const entity of inventoryDeleteEntities) {
                const response = await utilDeleteInventory(
                    props.isRetailer ? entity.supplierId : entity.merchantId,
                    encodeURIComponent(entity.supplierSku)
                );
                if (response !== true && response.errorMessage) {
                    errors.push(response.errorMessage);
                }
            }

            if (errors.length > 0) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: errors.join("\n")
                });
            }

            await new Promise(resolve => setTimeout(resolve, 750));
        } finally {
            setInventoryDeleteOpen(false);
            setInventoryDeleting(false);
            
            // Wait a bit to ensure state updates are complete
            await new Promise(resolve => setTimeout(resolve, 100));
            
            setInventoryConfig(prevConfig => ({...prevConfig, refresh: !prevConfig.refresh}));
        }
    };

    const zeroOutInventory = async () => {
        if (!hasInventoryManagePermission) return;

        handleBulkEdit(0, null, null, null);
    }

    const unmatchInventory = async () => {
        if (!hasInventoryManagePermission) return;

        handleBulkEdit(null, null, "", null);
    }

    const handleBulkEdit = async (quantity, cost, merchantSku, status) => {
        if (!hasInventoryManagePermission) return;

        setInventoryBulkUpdating(true);

        let errors = [];

        for (const entity of inventoryBulkEntities) {
            const existingInventory = await utilCheckInventory(
                props.isRetailer ? entity.supplierId : entity.merchantId,
                encodeURIComponent(entity.supplierSku)
            );
            if (existingInventory.errorMessage) {
                errors.push(existingInventory.errorMessage);
                continue;
            }

            const response = await utilUpdateInventory(
                props.isRetailer ? entity.supplierId : entity.merchantId,
                {
                    ...existingInventory,
                    ...(quantity !== null && {Quantity: quantity}),
                    ...(cost !== null && {Cost: cost}),
                    ...(merchantSku !== null && {MerchantSku: merchantSku}),
                    ...(!(!((status || "").trim())) && {Status: status.trim()})
                }
            );
            if (response !== true && response.errorMessage) {
                errors.push(response.errorMessage);
            }
        }

        if (errors.length > 0) {
            setError({
                open: true,
                errorHeader: "Error bulk updating inventory",
                errorMessage: errors.join("\n")
            });
        }

        await new Promise(resolve => setTimeout(resolve, 1250));
        setInventoryBulkOpen(false);
        setInventoryBulkAnchorEl(null);
        setInventoryConfig({...inventoryConfig, refresh: !inventoryConfig.refresh});
        setInventoryBulkUpdating(false);
    }

    const refreshInventory = async () => {
        await new Promise(resolve => setTimeout(resolve, 750));
        setInventoryConfig({...inventoryConfig, refresh: !inventoryConfig.refresh});
    }

    const loadEdiSpecifications = async () => {
        try {
            let url = Config.api + '/odata/Company/EDIPartnerSpecs';
            let response = await axios.get(url);
            const specifications = response.data.value;
            setEdiSpecifications(specifications);
        } catch (err) {
            setError({
                open: true,
                errorHeader: "Error Loading EDI Specifications",
                errorMessage: err.message
            });
        }
    }

    const loadHelpDesks = async () => {
        try {
            let res = (await axios.get(Config.api + "/odata/Company/Functions.GetCompanyInfo")).data;

            let helpDesks = (res.Helpdesks || []).sort((a, b) => a.CompanyName > b.CompanyName ? 1 : -1);
            setHelpDesks(helpDesks);
        } catch (e) {
            let error = null;
            if (e.response && e.response.data) {
                error = new Helpers().getApiErrors(e.response.data).join("\n")
            }
            if (!error && !e.response) {
                error = "Request failed: " + e.message;
            }

            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: error
            });

            return;
        }
    }

    const refreshInventorySummary = async (partnerId) => {
        if (!partnerId) {
            setInventorySummary({
                lastUpdate: "",
                lastUpdateCount: ""
            });
            return;
        }

        const summary = await utilGetInventorySummary(partnerId);
        if (summary.errorMessage) {
            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: summary.errorMessage
            });
        } else {
            setInventorySummary(summary);
        }
    }

    const setupValidationRules = async () => {
        let rules = (await axios.get(Config.api + "/odata/Company/ValidationDtos")).data.value || [];
        rules.forEach(r => {
            r.Rules.forEach(rule => {
                processRule(rule);
                (rule.Conditions || []).forEach(processRule);
                let oldConditions = rule.Conditions || [];
                rule.Conditions = oldConditions.filter(x => !(x.Type === "required" && x.Field === "Identifier.LogicbrokerKey"));
                rule.Validation = GetRuleDescription({...rule});
                rule.Required = rule.Type === "required" && rule.Conditions.length === 0;
                if(rule.Conditions.length !== oldConditions.length){
                    rule.Validation = (rule.Validation + "\nThis field may be automatically populated by Logicbroker.").trim();
                    rule.Required = true;
                }
            });
            r.Rules = r.Rules.sort((a, b) => {
                if(a.Required !== b.Required){
                    return a.Required && !b.Required ? -1 : 1;
                }
                return a.Field > b.Field ? 1 : a.Field < b.Field ? -1 : 0;
            });
        });

        const filteredRules = rules.filter(r => r.DocumentType === "Inventory");
        setRules(filteredRules);
    }

    const processRule = (rule) => {
        rule.DescriptiveField = rule.Field;
        rule.Parameters = rule.Parameters ? rule.Parameters.reduce((map, obj) => {map[obj.Key] = obj.Value; return map;}, {}) : null;
    }

    React.useEffect(() => {
        if(setTitle) {
            setTitle('Inventory Feed');
        }
        loadPartners();
        loadHelpDesks();
        loadEdiSpecifications();
        setupValidationRules();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Set default title on mount
    React.useEffect(() => {
        if (setTitle) {
            setTitle('Inventory Feed');
        }
    }, [setTitle]);

    return (
        <>
            <Root style={{gap: pageContentGap, padding: "24px 14px 24px 14px"}}>
                <Grid container justifyContent={"space-between"} direction={"row"} alignItems={"center"} style={{width: "100%"}}>
                    <Grid item>
                        <Header>
                            Inventory
                        </Header>
                    </Grid>

                    <Grid item style={{display: "flex", gap: "8px"}}>
                        {props.isRetailer && (
                            <Button
                                variant='outlined'
                                startIcon={<Sync style={{height: "18px", width: "18px"}}/>}
                                onClick={() => setRefreshModalOpen(true)}
                            >
                                Refresh
                            </Button>
                        )}

                        {isBillable && (
                            <Button
                                variant='outlined'
                                startIcon={<Settings style={{height: "18px", width: "18px"}}/>}
                                component={Link}
                                to={'/advanced-product-management/settings'}
                            >
                                Settings
                            </Button>
                        )}

                        <Button
                            variant='outlined'
                            startIcon={<ExportIcon style={{height: "18px", width: "18px"}}/>}
                            component={Link}
                            to={'/advanced-product-management/export'}
                        >
                            Export
                        </Button>

                        <Button
                            variant='outlined'
                            startIcon={<AddCircleOutline style={{height: "18px", width: "18px"}}/>}
                            onClick={() => {
                                setInventoryEditorOpen(true);
                                setInventoryEntity(null);
                            }}
                        >
                            Add Item
                        </Button>

                        {hasInventoryManagePermission && (
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<ImportIcon style={{height: "18px", width: "18px"}}/>}
                                component={Link}
                                to={'/advanced-product-management/import'}
                            >
                                Import
                            </Button>
                        )}
                    </Grid>
                </Grid>

                <Grid container style={{gap: pageContentGap}}>
                    <Paper>
                        <CatalogTab title={"Feed"}
                                    description={"View and manage inventory"}
                                    selected={currentTab === "feed"}
                                    onClick={() => updateTab("feed", true)}
                        />
                    </Paper>

                    <Paper>
                        <CatalogTab title={"Events"}
                                    description={"View uploaded/imported feeds and see errors"}
                                    selected={currentTab === "events"}
                                    onClick={() => updateTab("events", true)}
                        />
                    </Paper>
                </Grid>

                {loading ? (
                    <>
                        <ContentLoader preserveAspectRatio='none' style={{height: '100px', width: "100%"}}
                                       primaryColor='#e1e1e1'
                                       secondaryColor='#d8d8d8'/>

                        <ContentLoader preserveAspectRatio='none' style={{height: '100%', width: "100%"}}
                                       primaryColor='#e1e1e1'
                                       secondaryColor='#d8d8d8'/>
                    </>
                ) : (
                    <>
                        {currentTab === "feed" && (
                            <EnhancedTable
                                config={inventoryConfig} history={props.history} location={props.location}
                                key={"inventorymanagement_table"}
                                template={(table) => {
                                    inventoryTableRef.current = table;
                                    return (
                                        <CatalogManagerResults
                                            showSavedSearch={true}
                                            savedSearchTableType="advanced-product-management"
                                            bulkActions={
                                                hasInventoryManagePermission && (
                                                    <BulkActions
                                                        table={table}
                                                        setConfig={setInventoryConfig}
                                                        setEventConfig={setEventsConfig}
                                                        setInventoryDeleteEntities={setInventoryDeleteEntities}
                                                        setInventoryDeleteOpen={setInventoryDeleteOpen}
                                                        setInventoryBulkEntities={setInventoryBulkEntities}
                                                        setInventoryBulkOpen={setInventoryBulkOpen}
                                                        setInventoryBulkAnchorEl={setInventoryBulkAnchorEl}
                                                        setInventoryUnmatchConfirm={setInventoryUnmatchConfirm}
                                                        isBillable={isBillable}
                                                    />
                                                )
                                            }
                                            isSelectable={hasInventoryManagePermission}
                                            disableTermInUrl={true}
                                            disableLoadingPlaceholder={true}
                                            condensed={false}
                                            history={props.history} location={props.location}
                                            table={table}
                                            config={inventoryConfig} setConfig={setInventoryConfig}
                                            filterNullExceptions={filterNullExceptions}
                                            filterShowExceptions={["merchantSku"]}
                                            resultsContainerStyles={defaultResultsContainerStyles}
                                            renderButtonFiltersAfter={false}
                                            moreTableOptions={
                                                (row) =>
                                                    <TableMoreOpts
                                                        row={row}
                                                        setInventoryEntity={setInventoryEntity}
                                                        setInventoryEditorOpen={setInventoryEditorOpen}
                                                        setInventoryBulkEntities={setInventoryBulkEntities}
                                                        setInventoryBulkOpen={setInventoryBulkOpen}
                                                        setInventoryDeleteEntities={setInventoryDeleteEntities}
                                                        setInventoryDeleteOpen={setInventoryDeleteOpen}
                                                        hasInventoryManagePermission={hasInventoryManagePermission}
                                                        hasTieredReports={hasTieredReports}
                                                        setHistoryAnalyticsData={setHistoryAnalyticsData}
                                                    />
                                            }
                                            bufferExtensions={
                                                inventoryStatusSelection !== "all" && (
                                                    <Alert severity="info" style={{width: "100%"}}>
                                                        {inventoryStatusSelection === "matched"
                                                            ? (
                                                                <Typography>
                                                                    This view will show all items that have a retailer SKU.
                                                                    For more information on matched SKUs, see our {' '}
                                                                    <a
                                                                        href={"https://help.logicbroker.com/hc/en-us/articles/360022068531-Inventory-Feeds"}
                                                                        target={"_blank"} rel={"noopener noreferrer"}
                                                                    >
                                                                            Inventory
                                                                    </a> {' '}
                                                                    article.
                                                                </Typography>
                                                            )
                                                            : (
                                                                <Typography>
                                                                    This view will show all items that do not have a
                                                                    retailer SKU. For more information on matched SKUs, see
                                                                    our {' '}
                                                                    <a
                                                                        href={"https://help.logicbroker.com/hc/en-us/articles/360022068531-Inventory-Feeds"}
                                                                        target={"_blank"} rel={"noopener noreferrer"}
                                                                    >
                                                                        Inventory
                                                                    </a> {' '}
                                                                    article.
                                                                </Typography>
                                                            )}
                                                    </Alert>
                                                )
                                            }
                                            buttonFilters={
                                                <Grid container style={{gap: "0.5em", flex: "auto"}}>
                                                    <InventoryStatus
                                                        isRetailer={props.isRetailer}
                                                        partners={partners}
                                                        table={table}
                                                        entityCount={table.state.total}
                                                        setInventoryStatusSelection={setInventoryStatusSelection}
                                                        inventoryStatusSelection={inventoryStatusSelection}
                                                        setTitle={setTitle}
                                                    />
                                                </Grid>
                                            }
                                            tableMessage={
                                                table.data.length > 0 ? null : (
                                                    <TableCell colSpan={inventoryConfig.columns.length}>
                                                        <Typography style={{
                                                            textAlign: "center",
                                                            color: "#4F4D55",
                                                            margin: "1em 0"
                                                        }}>
                                                            <b>No inventory data found</b>
                                                        </Typography>
                                                    </TableCell>
                                                )
                                            }
                                        />
                                    );
                                }}
                            />
                        )}

                        {currentTab === "events" && (
                            <EnhancedTable config={eventsConfig} history={props.history} location={props.location}
                                           template={(table) => {
                                               eventsTableRef.current = table;
                                               return <>
                                                   {inventorySummary.lastUpdate && (
                                                       <Grid container style={{marginBottom: "-20px", gap: "1em"}}>
                                                           <Typography style={{color: "#4F4D55"}}>
                                                               <b>Last File Imported
                                                                   On:</b> {inventorySummary.lastUpdate}
                                                           </Typography>
                                                           <Typography style={{color: "#4F4D55"}}>
                                                               <b>Refreshed
                                                                   Items:</b> {inventorySummary.lastUpdateCount}
                                                           </Typography>
                                                       </Grid>
                                                   )}
                                                   <CatalogEventResults
                                                       disableOnViewAction={true}
                                                       history={props.history} location={props.location}
                                                       currentCatalog={currentTab}
                                                       table={table} config={eventsConfig}
                                                       setConfig={setEventsConfig}
                                                       showSavedSearch={true}
                                                       savedSearchTableType="inventory-events"
                                                       buttonFilters={
                                                           <Grid container style={{gap: "0.5em", flex: 1}}>
                                                               <InventoryEventsStatus
                                                                   table={table}
                                                                   partners={partners}
                                                                   refreshInventorySummary={refreshInventorySummary}
                                                               />
                                                           </Grid>
                                                       }
                                                   />
                                               </>;
                                           }}/>
                        )}
                    </>
                )}
            </Root>
            <InventoryEntity
                isRetailer={props.isRetailer}
                open={inventoryEditorOpen}
                setOpen={setInventoryEditorOpen}
                inventoryEntity={inventoryEntity}
                partners={partners}
                setError={setError}
                refreshInventory={refreshInventory}
                helpDesks={helpDesks}
                ediSpecifications={ediSpecifications}
                hasInventoryManagePermission={hasInventoryManagePermission}
                rules={rules}
            />
            <BulkEditPopper
                open={inventoryBulkOpen}
                anchorEl={inventoryBulkAnchorEl}
                onClose={() => {
                    setInventoryBulkOpen(false);
                    setInventoryBulkAnchorEl(null);
                }}
                handleBulkEdit={handleBulkEdit}
                zeroOutInventory={zeroOutInventory}
                setZeroOutConfirm={setInventoryZeroConfirm}
            />
            <UpdatingDialog
                open={inventoryDeleting || inventoryBulkUpdating}
                setOpen={inventoryDeleting ? setInventoryDeleting : setInventoryBulkUpdating}
                title={inventoryDeleting ? "Deleting Inventory..." : "Updating Inventory..."}
                subtitle={inventoryDeleting ? "Please wait while we delete the inventory..." : "Please wait while we update the inventory..."}
            />
            <EnhancedConfirmDialog
                open={inventoryZeroConfirm}
                setOpen={setInventoryZeroConfirm}
                title="Zero Out Items?"
                message="This action cannot be undone. Are you sure you want to update the quantity for all SKUs to 0?"
                type={"caution"}
                action={(action) => {
                    if (action) {
                        zeroOutInventory();
                    }
                    setInventoryZeroConfirm(false);
                    setInventoryBulkOpen(false);
                    setInventoryBulkAnchorEl(null);

                    setInventoryDeleteOpen(false);
                }}
            />
            <EnhancedConfirmDialog
                open={inventoryUnmatchConfirm}
                setOpen={setInventoryUnmatchConfirm}
                title="Unmatch Items?"
                message={(
                    <Typography>
                        Unmatching items will remove the Retailer SKU from the item and the item will no longer be
                        shared with the retailer.
                        Unmatch items that updates should no longer be sent for.
                        <br/>
                        <br/>
                        Reach out to your partner to review the process to discontinue items.
                    </Typography>
                )}
                type={"delete"}
                customConfirmText={"Unmatch"}
                action={(action) => {
                    if (action) {
                        unmatchInventory();
                    }
                    setInventoryUnmatchConfirm(false);
                    setInventoryBulkOpen(false);
                    setInventoryBulkAnchorEl(null);
                }}
            />
            <EnhancedConfirmDialog open={inventoryDeleteOpen} setOpen={setInventoryDeleteOpen}
                                   title={"Delete Inventory?"}
                                   message={(
                                       <Typography>
                                           Before deleting items, please be sure to zero out the inventory for those
                                           items. Failure to do so may cause stock outs with remaining quantity in the
                                           retailer's system. Please make sure these items are removed from future
                                           feeds.
                                           <br/>
                                           <br/>
                                           Confirm the process to discontinue items with your partner.
                                       </Typography>
                                   )}
                                   type={"delete"}
                                   action={(action) => {
                                       if (action) deleteInventory()
                                   }}
                                   extraActionsBetween={(
                                       <Button variant='text' onClick={() => {
                                           setInventoryZeroConfirm(true);
                                           setInventoryBulkOpen(false);
                                           setInventoryBulkAnchorEl(null);
                                       }}>
                                           Zero Out Inventory
                                       </Button>
                                   )}
            />
            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
            <InventoryHistoryAnalyticsDialog
                open={historyAnalyticsData.open}
                onClose={()=>{
                    setHistoryAnalyticsData(prev => ({ ...prev, open: false }));
                }}
                title="Inventory History"
                anchorPosition={historyAnalyticsData.anchor}
                data={historyAnalyticsData.data}
                reportConfig={props.tieredReport}
                viewMode={historyAnalyticsData.viewMode}              
                onViewModeChange={(mode) => setHistoryAnalyticsData(prev => ({ ...prev, viewMode: mode }))}
            >
            </InventoryHistoryAnalyticsDialog>
            {/* Refresh Supplier Data Modal */}
            <RefreshSupplierDataModal
                open={refreshModalOpen}
                onClose={() => setRefreshModalOpen(false)}
                availableSuppliers={partners}
                type="inventory"
                onSuccess={() => {
                    setRefreshModalOpen(false);
                    setInventoryConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                    setEventsConfig((prevState) => ({...prevState, refresh: !prevState.refresh}));
                }}
            />
        </>
    );
}

export default InventoryManagementCenter;
